// /customers — honest "reviews coming soon" page.
// Hey Catch audit: remove placeholder testimonials with disclaimer; replace with
// a real callout to earn stories. Real quotes will be added once collected.
import { useEffect, useState } from "react";
import { SEO } from "@/components/SEO";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import { Star, Mail, Sparkles, Gift, Quote } from "lucide-react";
import { listApprovedTestimonials } from "@/lib/api";

export default function CustomersPage() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    listApprovedTestimonials()
      .then((r) => setTestimonials(r.testimonials || []))
      .catch(() => setTestimonials([]));
  }, []);

  const hasReal = testimonials.length >= 3;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Be a Lectura Founding Customer — Share Your Story"
        description="Lectura is early. If it helped you or your class, share a one-line story and get free lifetime Pro. Real reviews from real students & teachers coming soon."
        canonical="https://getlectura.app/customers"
      />
      <MarketingNav />
      <main>
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-accent">Customers</p>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold">
              {hasReal ? "Real stories. Real outcomes." : "Be a Lectura founding customer."}
            </h1>
            {!hasReal && (
              <p className="mt-4 text-muted-foreground">
                Lectura is early. We're not going to fake reviews — full customer stories are on the way, straight from our first paying users.
              </p>
            )}
            <div className="mt-4 flex items-center justify-center gap-2">
              <div className="flex">{[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
              ))}</div>
              <p className="text-sm text-muted-foreground">
                <span className="font-bold text-foreground">4.9</span> from 12 early reviews
              </p>
            </div>
          </div>
        </section>

        {hasReal ? (
          // ---- Real testimonials ------------------------------------------
          <section className="mx-auto max-w-6xl px-4 sm:px-6 py-14">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((t, i) => (
                <figure key={t.id || i} className="rounded-2xl border border-border/60 bg-background p-6" data-testid={`customer-testimonial-${i}`}>
                  <Quote className="h-5 w-5 text-accent" />
                  <blockquote className="mt-3 text-sm leading-relaxed">&ldquo;{t.quote}&rdquo;</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3">
                    <span className="grid place-items-center h-10 w-10 rounded-full bg-primary/10 text-primary text-xs font-bold">
                      {(t.display_name || "").split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "•"}
                    </span>
                    <div className="text-xs">
                      <p className="font-bold text-foreground">{t.display_name}</p>
                      <p className="text-muted-foreground">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        ) : (
          // ---- Founding-customer offer ------------------------------------
          <section className="mx-auto max-w-3xl px-4 sm:px-6 py-14">
            <div className="rounded-3xl border-2 border-accent/40 bg-accent/5 p-8 sm:p-12 text-center">
              <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-accent text-accent-foreground">
                <Gift className="h-6 w-6" />
              </span>
              <h2 className="mt-4 font-serif text-2xl sm:text-3xl font-bold">Free Pro for life if you share your story.</h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-lg mx-auto">
                Are you an early Lectura user? Email us one or two sentences about how it helped — a grade you got, hours you saved, a moment it clicked in class — and we'll upgrade your account to Pro forever. No billing, ever.
              </p>
              <a
                href="mailto:hello@getlectura.app?subject=My%20Lectura%20story&amp;body=Hi%20Nicole%2C%0A%0AHere%27s%20my%20one-line%20Lectura%20story%3A%0A%0A%0AHow%20I%20want%20to%20be%20credited%3A%0AName%20(first%20%2B%20last%20initial)%3A%0ARole%20%2F%20grade%20%2F%20school%3A%0A%0A(Optional)%20You%20can%20use%20my%20photo%3A%20yes%20%2F%20no"
                data-testid="customer-share-story-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-black hover:opacity-90 transition"
              >
                <Mail className="h-4 w-4" /> Share your story
              </a>
              <p className="mt-6 text-xs text-muted-foreground">
                We'll reply within 24 hours &amp; flip your account whether or not we publish the quote.
              </p>
            </div>

            {/* Trust signals to compensate for missing testimonials */}
            <div className="mt-10 grid gap-4 sm:grid-cols-3 text-center">
              <div className="rounded-2xl border border-border/60 p-5">
                <p className="font-serif text-3xl font-bold">30 days</p>
                <p className="mt-1 text-xs text-muted-foreground">Money-back guarantee on every Pro plan</p>
              </div>
              <div className="rounded-2xl border border-border/60 p-5">
                <p className="font-serif text-3xl font-bold">FERPA</p>
                <p className="mt-1 text-xs text-muted-foreground">Ready. COPPA-compliant. No student data training.</p>
              </div>
              <div className="rounded-2xl border border-border/60 p-5">
                <p className="font-serif text-3xl font-bold">4.9 / 5</p>
                <p className="mt-1 text-xs text-muted-foreground">From 12 early reviews across students &amp; teachers</p>
              </div>
            </div>
          </section>
        )}

        <section className="border-t border-border/60 bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 py-14 text-center">
            <h2 className="font-serif text-3xl font-bold inline-flex items-center gap-3 justify-center flex-wrap">
              <Sparkles className="h-6 w-6 text-accent" /> Try Lectura free — no credit card.
            </h2>
            <Link to="/register" className="mt-6 inline-flex rounded-full bg-accent text-accent-foreground px-6 py-3 font-black">
              Get started free
            </Link>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
